import React,{useRef,useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {MobileView} from 'react-device-detect';
import {
  TranslateIcon,
  UploadIcon,
  ChevronDownIcon,
  LightningBoltIcon,
  CogIcon,
  SparklesIcon,
  XIcon,
  TrashIcon,
  DocumentIcon,
  ArchiveIcon,
  ExclamationCircleIcon,
  PlusIcon,
  ClipboardIcon,
} from '@heroicons/react/outline';
import {enqueueSnackbar} from 'notistack';
import {sprintf} from 'sprintf-js';
import getSortedLangsUtil from '../../utils/get-sorted-langs-util';
import eventManagerUtil from '../../utils/event-manager-util';
import wsManagerUtil from '../../utils/ws-manager-util';
import sendReqUtil from '../../utils/send-req-util';
import submitFormUtil from '../../utils/submit-form-util';
import dlUtil from '../../utils/dl-util';
import {useQuota} from '../../utils/use-quota-util';
import locales from '../../locales/locales';
import langMap from '../../constants/lang-map';
import engineMap from '../../constants/engine-map';
import plans from '../../constants/plans';
import config from '../../constants/config';
const Translator=({globalSettings})=>{
  const [targetLanguage,setTargetLanguage]=useState('en');
  const [translationEngine,setTranslationEngine]=useState('auto');
  const [isZip,setIsZip]=useState(false);
  const [selectedFiles,setSelectedFiles]=useState({});
  const [previews,setPreviews]=useState({});
  const [loadingMsg,setLoadingMsg]=useState(null);
  const [progress,setProgress]=useState(0);
  const [remTime,setRemTime]=useState(0);
  const [fileError,setFileError]=useState(null);
  const [isDragging,setIsDragging]=useState(false);
  const [isFocused,setIsFocused]=useState(false);
  const fileRef=useRef(null);
  const navigate=useNavigate();
  const quota=useQuota([globalSettings.token,loadingMsg===null]);
  const imageRegex=/^image\/(jpeg|png|webp)$/;
  const checkMangaState=async (shallSub)=>{
    const mangaId=localStorage.getItem('mangaId');
    if(!mangaId){
      return true;
    }
    if(shallSub){
      await wsManagerUtil.sendJson({
        name:'sub4manga',
        payload:mangaId,
      });
    }
    const res=await sendReqUtil(`/get-manga/${mangaId}`);
    if(res.status===200&&res.body){
      setTargetLanguage(res.body.tgt_lang);
      setTranslationEngine(res.body.engine);
      const newIsZip=localStorage.getItem('isZip')==='1';
      setIsZip(newIsZip);
      const elapsed=Date.now()-res.body.created_at*1000;
      if(res.body.state==='pending'){
        setProgress(0.95);
        setLoadingMsg(locales.translatorNormalizingContext);
        return false;
      }
      else if(res.body.state==='processing'){
        const estimatedTime=res.body.pages*config.EST_BETA+config.EST_GAMMA;
        const newProgress=Math.min(elapsed/estimatedTime,1)*0.95;
        setRemTime(estimatedTime-elapsed);
        setProgress(newProgress);
        setLoadingMsg(locales.translatorProcessing);
        return false;
      }
      else if(res.body.state==='completed'){
        enqueueSnackbar(
          locales.translatorCompleted[globalSettings.lang]
        ,{variant:'success'});
        const format=newIsZip?'zip':'pdf';
        dlUtil(`${config.API_URL}/static/mangas/${mangaId}/0.${format}`);
        setSelectedFiles({});
        console.log(`Task took ${elapsed/1000} seconds to complete`);
        if(res.body.src_lang===res.body.tgt_lang){
          enqueueSnackbar(
            locales.translatorSameLanguageWarning[globalSettings.lang],
          {variant:'warning',autoHideDuration:10000});
        }
        if(res.body.pages>res.body.translated_pages){
          enqueueSnackbar(
            sprintf(
              locales.translatorPartialPages[globalSettings.lang],
              res.body.translated_pages,
            )
          ,{variant:'warning',autoHideDuration:10000});
          navigate(`/${globalSettings.lang}/pricing`);
        }
      }
      else if(res.body.state==='failed'){
        setFileError({
          'unknown_error_ctx_norm':locales.translatorUnknownErrorCtxNorm,
          'timeout':locales.translatorTimeout,
          'unknown_error_util':locales.translatorUnknownErrorUtil,
          'filenames_not_numbers':locales.translatorFilenamesNotNumbers,
          'no_images':locales.translatorNoImages,
          'too_many_files':locales.translatorTooManyFiles,
          'unsupported_file_format':locales.translatorUnsupportedFileFormat,
          'extreme_aspect_ratio':locales.translatorExtremeAspectRatio,
          'unknown_error_service':locales.translatorUnknownErrorService,
        }[res.body.error]||Object.fromEntries(Object.keys(langMap).map(x=>[x,res.body.error])));
      }
      else{
        console.assert(false);
      }
    }
    if(res.status!=500){
      localStorage.removeItem('mangaId');
      setLoadingMsg(null);
    }
    return true;
  };
  useEffect(()=>{
    checkMangaState(true);
  },[]);
  useEffect(()=>{
    if(fileError){
      setFileError(null);
    }
    let newPreviews={};
    for(const [k,v] of Object.entries(previews)){
      if(selectedFiles[k]){
        newPreviews[k]=v;
      }
      else{
        URL.revokeObjectURL(v);
      }
    }
    for(const [k,v] of Object.entries(selectedFiles)){
      if(!previews[k]&&v.type.match(imageRegex)){
        newPreviews[k]=URL.createObjectURL(v);
      }
    }
    setPreviews(newPreviews);
  },[selectedFiles]);
  useEffect(()=>{
    if(engineMap[translationEngine].minLevel > (globalSettings.user?.level||0) && loadingMsg===null){
      setTranslationEngine('auto');
    }
  },[translationEngine,globalSettings.user,loadingMsg]);
  eventManagerUtil.deleteAllAndNewEvent('upload-progress-translator',(newProgress)=>{
    setProgress(newProgress);
    setLoadingMsg(locales.translatorUploading);
  });
  eventManagerUtil.deleteAllAndNewEvent('ws-manga_update',()=>{
    checkMangaState(false);
  });
  const updateSelectedFiles=(newSelectedFiles)=>{
    const files=Object.values(newSelectedFiles);
    if(!files.length){
      return;
    }
    if(files.length>600){
      setFileError(locales.translatorTooManyFiles);
      return;
    }
    if(Array.from(files).map(x=>x.size).reduce((a,b)=>a+b,0)>1e8){
      setFileError(locales.translatorTooBigFiles);
      return;
    }
    if(files.length>1){
      for(const x of files){
        if(!x.type.match(imageRegex)){
          setFileError(locales.translatorMultipleFilesMustBeImages);
          return;
        }
      }
    }
    else if(!files[0].type.match(imageRegex)&&
      files[0].type!=='application/zip'&&
      files[0].type!=='application/pdf'
    ){
      setFileError(locales.translatorInvalidFileType);
      return;
    }
    setSelectedFiles(newSelectedFiles);
  };
  const selectAdditionalFiles=(additionalFiles)=>{
    if(!additionalFiles.length){
      return;
    }
    const files={...selectedFiles};
    const keyOffset=Math.max(...Object.keys(files),0)+1;
    for(let i=0;i<additionalFiles.length;i++){
      files[i+keyOffset]=additionalFiles[i];
    }
    updateSelectedFiles(files);
  };
  const handleDrop=(e)=>{
    e.preventDefault();
    setIsDragging(false);
    if(loadingMsg!==null){
      // Shouldn't really happen though
      return;
    }
    selectAdditionalFiles(e.dataTransfer.files);
  };
  const handlePaste=async(e)=>{
    e.preventDefault();
    e.stopPropagation();
    if(loadingMsg!==null){
      // Shouldn't really happen though
      return;
    }
    const files=Array
      .from(e.clipboardData.items)
      .filter((x)=>x.kind==='file')
      .map((x)=>x.getAsFile())
      .filter((x)=>x!==null);
    if(files.length>0){
      setIsFocused(false);
      selectAdditionalFiles(await Promise.all(files.map(async (file)=>{
        return new File(
          [await file.arrayBuffer()],
          file.name,
          {type:file.type},
        );
      })));
    }
    else{
      setFileError(locales.translatorPasteUnsupported);
    }
  };
  const removeFile=(id)=>{
    let newSelectedFiles={...selectedFiles};
    delete newSelectedFiles[id];
    setSelectedFiles(newSelectedFiles);
  };
  const getFileIcon=(fileType)=>{
    if(fileType==='application/zip'){
      return <ArchiveIcon className='h-12 w-12 text-yellow-500' />;
    }
    else if(fileType==='application/pdf'){
      return <DocumentIcon className='h-12 w-12 text-red-500' />;
    }
  };
  const formatFileSize=(bytes)=>{
    if(bytes<1024){
      return bytes+' B';
    }
    else if(bytes<1048576){
      return (bytes/1024).toFixed(1)+' KB';
    }
    else{
      return (bytes/1048576).toFixed(1)+' MB';
    }
  };
  const handleSubmit=async(e)=>{
    e.preventDefault();
    if(loadingMsg){
      return;
    }
    if(!Object.keys(selectedFiles).length){
      setFileError(locales.translatorUploadFile);
      return;
    }
    if(quota<engineMap[translationEngine].cpp){
      enqueueSnackbar(
        locales.translatorNotEnoughCredits[globalSettings.lang]
      ,{variant:'error'});
      navigate(`/${globalSettings.lang}/pricing`);
      return;
    }
    setLoadingMsg(locales.translatorPreparing);
    const res=await submitFormUtil('/translate',{
      'tgt_lang':[targetLanguage,()=>{}],
      'engine':[translationEngine,()=>{}],
      'files':[Object.keys(selectedFiles).map(Number).sort((a,b)=>a-b).map(x=>selectedFiles[x]),setFileError],
    },{
      'ongoing_translation':{
        'files':locales.translatorOngoingTranslation,
      },
    },'form','translator');
    if(res){
      localStorage.setItem('mangaId',res.manga_id);
      localStorage.setItem('isZip',Number(isZip));
      checkMangaState(true);
    }
    else{
      setLoadingMsg(null);
    }
  };
  const handleTranslationEngineChange=(e)=>{
    e.preventDefault();
    if(engineMap[e.target.value].minLevel > (globalSettings.user?.level||0) && loadingMsg===null){
      enqueueSnackbar(
        engineMap[e.target.value].name+locales.translatorEngineNotInPlan[globalSettings.lang]
      ,{variant:'error'});
      navigate(`/${globalSettings.lang}/pricing`);
    }
    else{
      setTranslationEngine(e.target.value);
    }
  };
  const latinSorted=getSortedLangsUtil(globalSettings.lang);
  const isSubmitDisabled=loadingMsg||!Object.keys(selectedFiles).length;
  return (
    <form
      className='bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200 w-full'
      onSubmit={handleSubmit}
    >
      <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4'>
        <h2 className='text-xl sm:text-2xl font-bold text-gray-900'>
          {locales.translatorSettings[globalSettings.lang]}
        </h2>
        <div className={`flex items-center ${quota?'bg-purple-100':'bg-red-100'} px-4 py-2 rounded-full`}>
          {quota ?
            <LightningBoltIcon className='h-5 w-5 text-purple-800 mr-2'/> :
            <ExclamationCircleIcon className='h-5 w-5 text-red-800 mr-2'/>
          }
          <span className={`${quota?'text-purple-800':'text-red-800'} font-medium`}>
            {sprintf(
              locales.translatorCreditsRemaining[globalSettings.lang],
              (quota<0)?'...':quota)
            }
          </span>
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            {locales.translatorTargetLanguage[globalSettings.lang]}
          </label>
          <div className='relative'>
            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
              <TranslateIcon className='h-5 w-5 text-gray-400' />
            </div>
            <select
              value={targetLanguage}
              disabled={loadingMsg!==null}
              onChange={(e) => setTargetLanguage(e.target.value)}
              className='w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 appearance-none bg-white'>{
                latinSorted.map((x)=>(
                  <option
                    key={x}
                    value={x}
                  >{langMap[x].name[globalSettings.lang]}</option>
                ))
              }</select>
            <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
              <ChevronDownIcon className='h-5 w-5 text-gray-400' />
            </div>
          </div>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            {locales.translatorLLM[globalSettings.lang]}
          </label>
          <div className='relative'>
            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
              <CogIcon className='h-5 w-5 text-gray-400' />
            </div>
            <select 
              value={translationEngine}
              disabled={loadingMsg!==null}
              onChange={handleTranslationEngineChange}
              className='w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 appearance-none bg-white'>{
                Object.entries(engineMap).map(([k,v])=>{
                  const inParentheses=(
                    ((globalSettings.user?.level||0)<v.minLevel)?
                    sprintf(locales.translatorAtLeastPlan[globalSettings.lang],plans[v.minLevel].name):
                    `${v.cpp} cpp`
                  );
                  return (
                    <option
                      key={k}
                      value={k}
                    >{((typeof v.name)==='string')?v.name:v.name[globalSettings.lang]} ({inParentheses})</option>
                  );
                })
              }</select>
            <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
              <ChevronDownIcon className='h-5 w-5 text-gray-400' />
            </div>
          </div>
        </div>
      </div>
      <div className='mb-6'>
        <label className='block text-sm font-medium text-gray-700 mb-3'>
          {locales.translatorDesiredFormat[globalSettings.lang]}
        </label>
        <div className='flex w-full border border-gray-300 rounded-md overflow-hidden bg-white'>
          <button
            type='button'
            onClick={() => setIsZip(false)}
            disabled={loadingMsg!==null}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium transition-colors ${
              !isZip 
                ? 'bg-purple-600 text-white border-r border-purple-500' 
                : 'bg-white text-gray-700 hover:bg-gray-50 border-r border-gray-300'
            } ${loadingMsg ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <DocumentIcon className={`h-5 w-5 mr-2 ${!isZip ? 'text-white' : 'text-red-500'}`} />
            PDF
          </button>
          <button
            type='button'
            onClick={() => setIsZip(true)}
            disabled={loadingMsg!==null}
            className={`flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium transition-colors ${
              isZip 
                ? 'bg-purple-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } ${loadingMsg ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <ArchiveIcon className={`h-5 w-5 mr-2 ${isZip ? 'text-white' : 'text-yellow-500'}`} />
            ZIP
          </button>
        </div>
      </div>
      {loadingMsg===null&&(<div className='border-t border-gray-200 pt-6 mt-2'>
        <h3 className='text-lg font-semibold text-gray-900 mb-4'>
          {locales.translatorInputMangaSource[globalSettings.lang]}
        </h3>
        <div className='text-center'>
          <div
            tabIndex={0}
            onFocus={()=>setIsFocused(true)}
            onBlur={()=>setIsFocused(false)}
            onPaste={handlePaste}
            onDrop={handleDrop}
            onDragEnter={()=>{setIsDragging(true);}}
            onDragLeave={(e)=>{setIsDragging(e.currentTarget.contains(e.relatedTarget));}}
            onDragOver={(e)=>{e.preventDefault();}}
            className={`w-full border-2 rounded-lg duration-0 ${isFocused?'border-purple-600 bg-purple-50':(isDragging?'border-purple-400 bg-purple-50':((Object.keys(selectedFiles).length>0)?'border-purple-300 bg-purple-50':'border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100'))} ${(Object.keys(selectedFiles).length>0)?'p-4':'p-8 sm:p-12'}`}
          >
            {(Object.keys(selectedFiles).length>0)?(
              <>
                <div className='flex justify-between items-center mb-3'>
                  <h3 className='font-medium text-purple-800'>
                    {sprintf(
                      locales.translatorFilesSelected[globalSettings.lang],
                      Object.keys(selectedFiles).length
                    )}
                  </h3>
                  <div className='flex items-center gap-2'>
                    <MobileView>
                      <div className='relative'>
                        <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
                          <ClipboardIcon className='h-4 w-4 text-purple-700' />
                        </div>
                        <div
                          contentEditable={true}
                          suppressContentEditableWarning={true}
                          className='h-[30px] w-[30px] flex items-center justify-center text-center text-transparent caret-transparent bg-white border border-purple-300 rounded-md hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500'
                          onPaste={handlePaste}
                          onInput={(e)=>{e.currentTarget.textContent='\u00A0';}}
                        >
                          {'\u00A0'}
                        </div>
                      </div>
                    </MobileView>
                    <button
                      type='button'
                      className='inline-flex items-center justify-center p-1.5 text-purple-700 bg-white border border-purple-300 rounded-md hover:bg-purple-100'
                      onClick={()=>{fileRef.current.click();}}
                    >
                      <PlusIcon className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      className='inline-flex items-center justify-center p-1.5 text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50'
                      onClick={(e)=>{e.currentTarget.blur();setSelectedFiles({});}}
                    >
                      <TrashIcon className='h-4 w-4' />
                    </button>
                  </div>
                </div>
                <div className='max-h-64 overflow-y-auto p-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'>
                  {Object.entries(selectedFiles).map(([id,file])=>(
                    <div key={id} className='relative border border-gray-200 rounded-md p-2 bg-white'>
                      <button 
                        type='button'
                        onClick={(e)=>{e.currentTarget.blur();removeFile(id);}}
                        className='absolute -top-2 -right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200'
                      >
                        <XIcon className='h-4 w-4' />
                      </button>
                      <div className='h-24 flex items-center justify-center mb-2 bg-gray-50 rounded overflow-hidden'>
                        {previews[id]?(
                          <img 
                            src={previews[id]} 
                            alt={file.name}
                            className='h-full w-full object-contain'
                          />
                        ):(
                          getFileIcon(file.type)
                        )}
                      </div>
                      <div className='text-xs truncate text-center' title={file.name}>
                        {file.name}
                      </div>
                      <div className='text-xs text-gray-500 text-center'>
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ):(
                <div className='space-y-4'>
                  <div className='mx-auto h-12 w-12 text-gray-400'>
                    <UploadIcon className={`h-12 w-12 mx-auto ${isDragging ? 'text-purple-500' : 'text-gray-400'}`} />
                  </div>
                  <div className='text-center space-y-2'>
                    <p className='text-sm text-gray-600'>
                      {locales.translatorUploadWaysPrefix[globalSettings.lang]}
                      <span className='text-purple-700 font-medium'>
                        {locales.translatorUploadWaysHighlight[globalSettings.lang]}
                      </span>
                      {locales.translatorUploadWaysSuffix[globalSettings.lang]}
                    </p>
                    <p className='text-xs text-gray-500'>
                      {locales.translatorFileTypes[globalSettings.lang]}
                    </p>
                  </div>
                  <div className='flex flex-col items-center gap-3'>
                    <button
                      type='button'
                      className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-800 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                      onClick={()=>{fileRef.current.click();}}
                    >
                      <UploadIcon className='mr-2 h-5 w-5' />
                      {locales.translatorSelectFiles[globalSettings.lang]}
                    </button>
                    <MobileView>
                      <div className='flex flex-col items-center gap-3'>
                        <span className='text-xs text-gray-400 uppercase tracking-wide'>
                          {locales.translatorOr[globalSettings.lang]}
                        </span>
                        <div className='relative w-64'>
                          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                            <ClipboardIcon className='h-5 w-5 text-purple-700' />
                          </div>
                          <div
                            contentEditable={true}
                            suppressContentEditableWarning={true}
                            className='w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm text-left text-transparent caret-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-600 whitespace-nowrap overflow-hidden'
                            onPaste={handlePaste}
                            onInput={(e)=>{e.currentTarget.textContent='';}}
                          />
                          <span className='absolute inset-y-0 left-10 right-3 flex items-center text-sm text-gray-400 pointer-events-none truncate'>
                            {locales.translatorPasteFromClipboard[globalSettings.lang]}
                          </span>
                        </div>
                      </div>
                    </MobileView>
                  </div>
                </div>
            )}
          </div>
          {fileError&&(
            <div className='mt-3 text-sm text-red-600'>
              <span>{fileError[globalSettings.lang]}</span>
            </div>
          )}
        </div>
      </div>)}
      <div className='mt-6 space-y-3'>
        <button 
          type='submit'
          disabled={isSubmitDisabled}
          className={`w-full bg-purple-800 ${isSubmitDisabled?'opacity-70 cursor-not-allowed':'hover:bg-purple-600'} text-white px-4 py-3 rounded-lg text-lg font-medium flex items-center justify-center relative overflow-hidden`}
        >
          <div className='z-10 flex items-center justify-center'>
            {
              loadingMsg?
              loadingMsg[globalSettings.lang]:
              locales.translatorStartTranslation[globalSettings.lang]
            }
            <SparklesIcon className='ml-2 h-5 w-5' />
          </div>
          {loadingMsg&&(
            <div
              className='absolute top-0 left-0 h-full bg-purple-600'
              style={{
                width:`${progress*100}%`,
                animation:loadingMsg?.en==='Processing...'?`progress-anim ${remTime}ms linear forwards`:undefined,
              }}
            />
          )}
        </button>
        <div className='mt-2 text-xs text-gray-400'>
          {locales.translatorCppMeaning[globalSettings.lang]}
        </div>
      </div>
      <input
        ref={fileRef}
        type='file'
        accept='image/jpeg,image/png,image/webp,application/zip,application/pdf'
        multiple={true}
        onChange={(e)=>{selectAdditionalFiles(e.target.files);}}
        className='hidden'
      />
    </form>
  );
};
export default Translator;

